import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Box, Paper, Typography, Divider, Stack } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { GetChangeDto } from '@codefluegel/zeta-change-typescript-client';
import { showImplementation } from '../../utils/permissionHelper';
import { formatNumber } from '../../utils/getTotalTime';
import { useChangeStore } from '../../store/changeStore';

const NumbersPanel = observer(({ change }: { change: GetChangeDto }) => {
  const { t } = useTranslation();

  const isImplementationVisible = showImplementation(change);

  const {
    implementedCount,
    hoursValues,
    hoursCosts,
    materialValues,
    totalSumValues,
    workPackagesCount,
  } = useChangeStore().calculationStore;

  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1.2fr repeat(3, 1fr)',
          gap: { xs: 1, sm: 2 }, // Reduce gap on small screens
          textAlign: 'center',
          overflowX: 'auto', // Add horizontal scroll for very small viewports
          pb: 1, // Add some padding at the bottom for the scrollbar
        }}
      >
        {/* Header Row */}
        <Box />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <AccessTimeIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="body1" fontWeight="bold">
            {t('costs.evaluated')}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <CalculateIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="body1" fontWeight="bold">
            {t('costs.calculated')}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
          <Typography variant="body1" fontWeight="bold">
            {t('costs.released')}
          </Typography>
        </Stack>

        {/* Hours / Costs Row - Correctly split and formatted */}
        <Typography
          variant="body2"
          sx={{ textAlign: 'left', pl: 1, fontWeight: 'bold' }}
        >
          {t('costs.costTable.hoursCosts')}
        </Typography>
        {hoursValues.map((val, index) => (
          <Stack
            key={uuidv4()}
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              minWidth: 0, // Allow flex items to shrink
              wordBreak: 'break-word', // Allow long numbers to wrap
            }}
          >
            <Typography variant="body2">{formatNumber(val ?? 0, 0)}</Typography>
            <Typography variant="body2">
              {formatNumber(hoursCosts[index] ?? 0)}
            </Typography>
          </Stack>
        ))}

        {/* Material Cost Row */}
        <Typography
          variant="body2"
          sx={{ textAlign: 'left', pl: 1, fontWeight: 'bold' }}
        >
          {t('costs.costTable.materialCost')}
        </Typography>
        {materialValues.map(val => (
          <Typography
            variant="body2"
            key={uuidv4()}
            sx={{ wordBreak: 'break-word', minWidth: 0 }}
          >
            {formatNumber(val)}
          </Typography>
        ))}

        {/* Total Sum Row */}
        <Typography
          variant="body1"
          sx={{
            textAlign: 'left',
            pl: 1,
            fontWeight: 'bold',
            mt: 1,
            alignSelf: 'center',
          }}
        >
          {t('costs.costTable.totalSum')}
        </Typography>
        {totalSumValues.map(val => (
          <Box
            key={uuidv4()}
            sx={{
              p: 1,
              bgcolor: 'grey.100',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              fontWeight: 'bold',
              mt: 1,
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              {formatNumber(val)}
            </Typography>
          </Box>
        ))}
      </Box>

      {isImplementationVisible && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('costs.costTable.wpImplemented')}: {implementedCount} /{' '}
              {workPackagesCount}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
});

export default NumbersPanel;
